import { FormField, instanceOfFormFieldSection, ViewModel } from '@sitecore-jss/sitecore-jss-forms';

import { ACTION_CONSTS, MATCH_TYPE_CONSTS, OPERATOR_CONSTS } from '../constants/formConditions';
import { FieldCondition, FieldModelWithConditions, FieldValuesMap } from '../types/formConditions';

type NormalizedValue = string | number | boolean | string[];

/**
 * Normalizes a value based on the operator type
 * @param value - The value to normalize
 * @param operatorId - The operator ID to determine normalization strategy
 * @returns Normalized value as string, number, boolean, or string array
 */
function normalizeValue(value: unknown, operatorId: string): NormalizedValue {
  if (value == null) {
    return '';
  }

  if (Array.isArray(value)) {
    return value.map((val) => String(val).trim().toLowerCase());
  }

  if (typeof value === 'boolean') {
    return value;
  }

  // Numeric operators - convert to number
  switch (operatorId) {
    case OPERATOR_CONSTS.IS_GREATER_THAN:
    case OPERATOR_CONSTS.IS_GREATER_THAN_OR_EQUAL_TO:
    case OPERATOR_CONSTS.IS_LESS_THAN:
    case OPERATOR_CONSTS.IS_LESS_THAN_OR_EQUAL_TO:
      return Number(value) || 0;
    default:
      return String(value);
  }
}

/**
 * Operator functions for different comparison types
 * Maps operator IDs to their corresponding comparison functions
 * @param fieldValue - The normalized field value to compare
 * @param expected - The normalized expected value to compare against
 * @returns True if the comparison matches the operator's logic, false otherwise
 */
const operatorFunctions: Record<
  string,
  (fieldValue: NormalizedValue, expected: NormalizedValue) => boolean
> = {
  [OPERATOR_CONSTS.IS_EQUAL_TO]: (fieldValue, expected) => {
    if (Array.isArray(fieldValue) && Array.isArray(expected)) {
      return (
        fieldValue.length === expected.length && fieldValue.every((val) => expected.includes(val))
      );
    }
    return fieldValue === expected;
  },
  [OPERATOR_CONSTS.IS_NOT_EQUAL_TO]: (fieldValue, expected) => {
    if (Array.isArray(fieldValue) && Array.isArray(expected)) {
      return !(
        fieldValue.length === expected.length && fieldValue.every((val) => expected.includes(val))
      );
    }
    return fieldValue !== expected;
  },
  [OPERATOR_CONSTS.CONTAINS]: (fieldValue, expected) => {
    if (Array.isArray(fieldValue)) {
      return fieldValue.some((val) => String(val).includes(String(expected)));
    }
    return String(fieldValue).includes(String(expected));
  },
  [OPERATOR_CONSTS.DOES_NOT_CONTAIN]: (fieldValue, expected) => {
    if (Array.isArray(fieldValue)) {
      return !fieldValue.some((val) => String(val).includes(String(expected)));
    }
    return !String(fieldValue).includes(String(expected));
  },
  [OPERATOR_CONSTS.STARTS_WITH]: (fieldValue, expected) => {
    if (Array.isArray(fieldValue)) {
      return fieldValue.some((val) => String(val).startsWith(String(expected)));
    }
    return String(fieldValue).startsWith(String(expected));
  },
  [OPERATOR_CONSTS.DOES_NOT_START_WITH]: (fieldValue, expected) => {
    if (Array.isArray(fieldValue)) {
      return !fieldValue.some((val) => String(val).startsWith(String(expected)));
    }
    return !String(fieldValue).startsWith(String(expected));
  },
  [OPERATOR_CONSTS.ENDS_WITH]: (fieldValue, expected) => {
    if (Array.isArray(fieldValue)) {
      return fieldValue.some((val) => String(val).endsWith(String(expected)));
    }
    return String(fieldValue).endsWith(String(expected));
  },
  [OPERATOR_CONSTS.DOES_NOT_END_WITH]: (fieldValue, expected) => {
    if (Array.isArray(fieldValue)) {
      return !fieldValue.some((val) => String(val).endsWith(String(expected)));
    }
    return !String(fieldValue).endsWith(String(expected));
  },
  [OPERATOR_CONSTS.IS_GREATER_THAN]: (fieldValue, expected) =>
    Number(fieldValue) > Number(expected),
  [OPERATOR_CONSTS.IS_GREATER_THAN_OR_EQUAL_TO]: (fieldValue, expected) =>
    Number(fieldValue) >= Number(expected),
  [OPERATOR_CONSTS.IS_LESS_THAN]: (fieldValue, expected) => Number(fieldValue) < Number(expected),
  [OPERATOR_CONSTS.IS_LESS_THAN_OR_EQUAL_TO]: (fieldValue, expected) =>
    Number(fieldValue) <= Number(expected),
};

/**
 * Validates a single condition against a field value
 * @param operatorId - The operator ID from Sitecore condition settings
 * @param fieldValue - The current value of the field being evaluated
 * @param valueAgainst - The expected value to compare against
 * @returns True if the condition is met, false otherwise
 */
function evaluateCondition(
  operatorId: string,
  fieldValue: NormalizedValue,
  valueAgainst: string
): boolean {
  const operatorFn = operatorFunctions[operatorId];
  if (!operatorFn) {
    return false;
  }

  const normalizedField = normalizeValue(fieldValue, operatorId);
  const normalizedExpected = normalizeValue(valueAgainst, operatorId);

  if (typeof normalizedField === 'boolean' || typeof normalizedExpected === 'boolean') {
    return Boolean(normalizedField) === Boolean(normalizedExpected);
  }

  return operatorFn(normalizedField, normalizedExpected);
}

/**
 * Main function to check if a field should be hidden based on its conditions
 * @param currentFieldKey - The fieldKey of the current field (from conditionSettings)
 * @param fieldConditions - The field's condition settings from Sitecore
 * @param fieldValues - Current values of all form fields
 * @returns true if field should be hidden, false if it should be shown
 */
export function checkConditions(
  currentFieldKey: string | undefined,
  fieldConditions: FieldCondition[] | undefined,
  fieldValues: FieldValuesMap
): boolean {
  if (!fieldConditions || fieldConditions.length === 0) {
    return false;
  }

  return fieldConditions.some((conditionGroup) => {
    const { conditions, actions, matchTypeId } = conditionGroup;

    if (!conditions || !actions || actions.length === 0) {
      return false;
    }

    // Evaluate all conditions and combine based on match type
    const conditionResults = conditions.map((condition) =>
      evaluateCondition(condition.operatorId, fieldValues[condition.fieldId] || '', condition.value)
    );

    const conditionsMet =
      matchTypeId === MATCH_TYPE_CONSTS.ALL
        ? conditionResults.every(Boolean)
        : conditionResults.some(Boolean);

    // Check if any action targeting this field should hide it
    return actions.some((action) => {
      if (action.fieldId !== currentFieldKey) {
        return false;
      }

      const isShow = action.actionTypeId === ACTION_CONSTS.SHOW;
      return isShow ? !conditionsMet : conditionsMet;
    });
  });
}

/**
 * Builds field maps for conditional rendering
 * @param fields - Array of form fields from Sitecore JSS
 * @returns Object containing fieldKeyMap (itemId to fieldKey mapping) and initialFieldValues (fieldKey to empty string mapping)
 */
export function buildFieldMaps(fields: FormField[]): {
  fieldKeyMap: { [itemId: string]: string };
  initialFieldValues: FieldValuesMap;
} {
  const fieldKeyMap: { [itemId: string]: string } = {};
  const initialFieldValues: FieldValuesMap = {};

  function processFields(fieldsArray: FormField[]) {
    fieldsArray.forEach((field) => {
      const model = field.model as ViewModel & FieldModelWithConditions;
      const { itemId, conditionSettings } = model;
      const { fieldKey } = conditionSettings;

      if (itemId && fieldKey) {
        fieldKeyMap[itemId] = fieldKey;
        initialFieldValues[fieldKey] = '';
      }

      //process nested fields in sections
      if (instanceOfFormFieldSection(field)) {
        processFields(field.fields);
      }
    });
  }

  processFields(fields);
  return { fieldKeyMap, initialFieldValues };
}
